import { useState } from "react";
import type { FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/dah956joh653ivfvnov0";

interface SubscribeFormProps {
  tone?: "light" | "dark";
}

export default function SubscribeForm({ tone = "light" }: SubscribeFormProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const isDark = tone === "dark";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);
    const honey = String(payload.get("website_alt") ?? "").trim();
    const email = String(payload.get("email") ?? "").trim();

    if (honey) {
      setStatus("success");
      setMessage("订阅成功，有新合集时我们会第一时间通知你。");
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("请填写正确的邮箱地址。");
      return;
    }

    setStatus("submitting");
    setMessage("");

    try {
      const body = new URLSearchParams();
      body.append("email", email);
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const lowered = String(serverMsg).toLowerCase();

      if (response.ok && code === "OK" && !lowered.includes("spam")) {
        setStatus("success");
        setMessage("订阅成功，有新合集时我们会第一时间通知你。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(String(serverMsg) || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后再试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          name="email"
          required
          placeholder="留下邮箱，接收每周合集更新"
          className={`h-11 flex-1 rounded-md border px-4 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 ${
            isDark
              ? "border-secondary-700 bg-secondary-950/40 text-background-50 placeholder:text-secondary-300 focus:border-primary-400"
              : "border-background-300 bg-background-50 text-foreground-900 focus:bg-background-50"
          }`}
        />
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-anti-bot-field"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
        >
          {status === "submitting" ? "提交中…" : "订阅更新"}
        </button>
      </div>
      {message && (
        <p
          className={`mt-3 text-xs ${
            status === "error"
              ? isDark
                ? "text-accent-300"
                : "text-accent-600"
              : isDark
                ? "text-secondary-200"
                : "text-secondary-700"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}